// internal/InternalLayout.jsx — Shell AYA Internal (sidebar premium sombre, réf. Creator Studio).
import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Sparkles, FolderKanban, Users, Bot, Settings2, Store, UserRound, BarChart3, Target, Brain, Clapperboard, Rocket, Activity, ShieldCheck, ScrollText, Settings, ChevronLeft, MapPinned } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

export const IT = {
  bg: "#0A0F0C", panel: "#10, 23, 19", card: "#101713", border: "rgba(46,226,123,0.14)",
  green: "#2EE27B", gold: "#E8B84B", text: "#ECF5EE", muted: "#8BA394",
};
export const S = {
  card: { background: "#101713", border: "1px solid rgba(46,226,123,0.14)", borderRadius: 14 },
  h2: { fontSize: 15, fontWeight: 800, color: "#ECF5EE", margin: 0 },
  btn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: "none", background: "linear-gradient(180deg,#3BE07A,#1DB954)", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  ghost: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(46,226,123,0.3)", background: "rgba(46,226,123,0.07)", color: "#2EE27B", fontSize: 12.5, fontWeight: 800, cursor: "pointer", textDecoration: "none" },
};

const NAV = [
  { to: "/internal", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/internal/creator", icon: Sparkles, label: "Creator Studio" },
  { icon: FolderKanban, label: "Projets", soon: true },
  { to: "/internal/workspaces", icon: Users, label: "Workspaces" },
  { to: "/internal/agents", icon: Bot, label: "Agents IA" },
  { to: "/internal/operations", icon: Settings2, label: "Opérations" },
  { to: "/internal/operations/pro-validation", icon: Store, label: "AYA Pro", sub: true },
  { icon: UserRound, label: "Utilisateurs", soon: true },
  { icon: BarChart3, label: "Analytics", soon: true },
  { icon: Target, label: "Objectifs", soon: true },
  { to: "/internal/knowledge", icon: Brain, label: "Knowledge" },
  { to: "/internal/personnalites", icon: UserRound, label: "Fiches AYA ID" },
  { to: "/internal/fiches", icon: MapPinned, label: "Fiches Search" },
  { icon: Clapperboard, label: "Media Studio", soon: true },
  { icon: Rocket, label: "Déploiements", soon: true },
  { icon: Activity, label: "Monitoring", soon: true },
  { to: "/internal/security", icon: ShieldCheck, label: "Sécurité" },
  { to: "/internal/logs", icon: ScrollText, label: "Logs & Audit" },
  { icon: Settings, label: "Paramètres", soon: true },
];

export default function InternalLayout({ children, title, subtitle }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [denied, setDenied] = useState(false);
  const [me, setMe] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) { navigate("/login?next=/internal"); return; }
    if (user && user.role && user.role !== "owner" && user.role !== "collaborator") setDenied(true);
  }, [isAuthenticated, user, navigate]);
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/internal/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` } })
      .then((r) => r.json()).then((d) => d.ok && setMe(d)).catch(() => {});
    // Secure Welcome : enrôlement obligatoire avant d'entrer dans AYA Internal.
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/internal/identity/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` } })
      .then((r) => r.json()).then((d) => { if (d.ok && !d.enrolled) navigate("/welcome", { replace: true }); setLockMin(d.lock_timeout_min ?? 5); }).catch(() => {});
  }, [isAuthenticated, navigate]);
  // Verrouillage automatique par inactivité (§12-14) + réouverture rapide auditée.
  const [locked, setLocked] = useState(false);
  const [lockMin, setLockMin] = useState(5);
  const [reopening, setReopening] = useState(false);
  useEffect(() => {
    let timer;
    const reset = () => { clearTimeout(timer); if (!locked) timer = setTimeout(() => { setLocked(true); fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/internal/identity/session-event`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` }, body: JSON.stringify({ event: "session_locked" }) }).catch(() => {}); }, Math.max(1, lockMin) * 60000); };
    const evs = ["mousemove", "keydown", "click", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => { clearTimeout(timer); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [locked, lockMin]);
  const unlock = () => {
    setReopening(true);
    setTimeout(() => {
      setLocked(false); setReopening(false);
      fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/internal/identity/session-event`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` }, body: JSON.stringify({ event: "session_unlocked", via: "quick_reverify" }) }).catch(() => {});
    }, 1600);
  };
  const isCollab = user?.role === "collaborator";
  const nav = isCollab
    ? [
        { to: me?.workspace ? `/internal/ws/${me.workspace.slug}` : "/internal/workspaces", icon: LayoutDashboard, label: "Mon espace" },
        { to: "/internal/knowledge", icon: Brain, label: "Knowledge" },
      ]
    : NAV;

  if (denied) return (
    <div style={{ minHeight: "100vh", background: IT.bg, color: IT.text, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 }} data-testid="internal-denied">
      <ShieldCheck size={34} color="#F87171" />
      <div style={{ fontWeight: 800 }}>Accès réservé au CEO (rôle owner).</div>
      <button style={S.ghost} onClick={() => navigate("/search")}>Retour à AYA</button>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: IT.bg, color: IT.text, display: "flex", fontFamily: "inherit" }} data-testid="internal-shell">
      {locked && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(4,8,6,0.94)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }} data-testid="session-lock">
          <div style={{ width: 64, height: 64, borderRadius: 99, background: "rgba(46,226,123,0.1)", border: "1px solid rgba(46,226,123,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>🔒</div>
          <div style={{ fontWeight: 900, fontSize: 16 }}>Session verrouillée pour votre sécurité</div>
          <div style={{ fontSize: 12, color: IT.muted }}>{reopening ? "Vérification rapide : appareil ✓ · session ✓ · contexte…" : `Inactivité de plus de ${lockMin} min détectée.`}</div>
          <button data-testid="session-unlock" onClick={unlock} disabled={reopening} style={{ ...S.btn, padding: "12px 24px" }}>{reopening ? "⏳ Réouverture…" : "Réouvrir ma session"}</button>
        </div>
      )}
      <aside className="aya-int-sidebar" style={{ width: 218, flexShrink: 0, background: "#070B09", borderRight: `1px solid ${IT.border}`, padding: "16px 10px", position: "sticky", top: 0, height: "100vh", overflowY: "auto", scrollbarWidth: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 8px 14px" }}>
          <img src="/aya/splash.png" alt="AYA" style={{ width: 34, height: 34, objectFit: "contain" }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, letterSpacing: 1.5 }}>AYA</div>
            <div style={{ fontSize: 8.5, color: IT.green, fontWeight: 800, letterSpacing: 0.8 }}>INTERNAL</div>
          </div>
        </div>
        {isCollab && me?.workspace && (
          <div style={{ margin: "0 4px 10px", padding: "8px 10px", borderRadius: 10, background: `${me.workspace.color}14`, border: `1px solid ${me.workspace.color}44`, fontSize: 11, fontWeight: 800, color: me.workspace.color }} data-testid="int-collab-badge">
            {me.workspace.name}
            <span style={{ display: "block", fontSize: 9.5, color: IT.muted, fontWeight: 600 }}>{me.display_name}</span>
          </div>
        )}
        {nav.map((n) => n.to ? (
          <NavLink key={n.label} to={n.to} end={n.end} data-testid={`int-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            style={({ isActive }) => ({
              display: "flex", alignItems: "center", gap: 9, padding: "9px 10px", marginBottom: 2, marginLeft: n.sub ? 14 : 0,
              borderRadius: 10, fontSize: 12.5, fontWeight: 700, textDecoration: "none",
              color: isActive ? "#FFFFFF" : IT.muted,
              background: isActive ? "rgba(46,226,123,0.12)" : "transparent",
              border: isActive ? "1px solid rgba(46,226,123,0.35)" : "1px solid transparent",
            })}>
            <n.icon size={15} /> {n.label}
          </NavLink>
        ) : (
          <div key={n.label} title="Session 2/3" style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 10px", marginBottom: 2, borderRadius: 10, fontSize: 12.5, fontWeight: 700, color: "rgba(139,163,148,0.45)", cursor: "default" }}>
            <n.icon size={15} /> {n.label}
            <span style={{ marginLeft: "auto", fontSize: 8, fontWeight: 900, color: IT.gold, border: `1px solid rgba(232,184,75,0.4)`, borderRadius: 999, padding: "1px 6px" }}>S2</span>
          </div>
        ))}
        <button onClick={() => navigate("/search")} style={{ ...S.ghost, width: "100%", justifyContent: "center", marginTop: 12, boxSizing: "border-box" }} data-testid="int-back-aya">
          <ChevronLeft size={13} /> Retour à AYA
        </button>
      </aside>
      <main style={{ flex: 1, minWidth: 0, padding: "18px 22px 40px" }}>
        <header style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 24, fontWeight: 900, margin: 0 }}>{title}</h1>
          {subtitle && <div style={{ fontSize: 12.5, color: IT.muted, marginTop: 3 }}>{subtitle}</div>}
        </header>
        {children}
      </main>
    </div>
  );
}
